// useDelayedFlag
// ---------------
// Returns true only once `isActive` has been continuously true for
// `delayMs`. Flips straight back to false the moment `isActive` does.
//
// This is what keeps fast work silent. A loading treatment that appears
// for 80ms and disappears again reads as a flicker — the user registers
// that *something* moved without ever learning what, which is worse than
// showing nothing at all. Gate the treatment behind this hook and a load
// that beats the threshold is simply invisible; only a wait long enough
// to be noticed gets an explanation.
//
// Deliberately not a debounce: the flag rises late but falls
// immediately, so content never sits behind a skeleton that has already
// served its purpose.

import { useEffect, useState } from "react";

export function useDelayedFlag(isActive: boolean, delayMs: number): boolean {
  const [delayHasElapsed, setDelayHasElapsed] = useState<boolean>(false);

  useEffect(() => {
    if (!isActive) {
      setDelayHasElapsed(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDelayHasElapsed(true);
    }, delayMs);

    return () => window.clearTimeout(timeoutId);
  }, [isActive, delayMs]);

  return isActive && delayHasElapsed;
}
